// Layer 1: Registry Standard (NAACCR, AHA/ACC, etc.)
export interface RegistryStandard {
    id: string;
    code: string;
    name: string;
    description: string | null;
    registry_domain: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

// Standard Version (v24, 2024-update, etc.)
export interface StandardVersion {
    id: string;
    registry_standard_id: string;
    version_code: string;
    display_name: string;
    version_year: number | null;
    release_date: string | null;
    effective_date: string;
    end_date: string | null;
    description: string | null;
    is_active: boolean;
    published_at: string | null; // once set, version is immutable
    total_fields: number;
    created_at: string;
    updated_at: string;
}

// Standard Field Definition (Item #390, EF-001, etc.)
// All columns from the authoritative DataItems.csv are preserved.
export interface StandardFieldDefinition {
    id: string;
    standard_version_id: string;
    // Core identification
    item_number: string;
    name: string; // XML NAACCR ID (e.g., 'dateOfDiagnosis')
    display_name: string; // Data Item Name from CSV
    xml_naaccr_id: string | null; // explicit XML tag provenance
    parent_xml_element: string | null; // 'NaaccrData' | 'Patient' | 'Tumor'
    // Classification & ordering
    category: string | null; // Section Name from CSV
    source_of_standard: string | null; // 'SEER/CoC', 'NAACCR', 'SEER'
    record_types: string | null; // 'A,C,I,M'
    sort_order: number;
    start_column: number | null; // flat file record position
    // Data type & constraints
    data_type: string | null; // 'digits', 'text', 'date', 'mixed', 'alpha', 'numeric'
    data_length: number | null;
    format: string | null; // 'YYYYMMDD', 'Right justified, zero filled'
    allowed_values: Record<string, unknown> | null; // range constraints (enumerated codes in StandardFieldValue)
    // Documentation
    description: string | null;
    rationale: string | null;
    general_notes: string | null;
    instructions_for_coding: string | null; // CRITICAL for extraction guidance
    code_notes: string | null;
    clarification: string | null;
    // Required status (base + per-program raw values)
    is_required: boolean; // true if required by ALL programs
    npcr_collect: string | null; // 'R', 'R*', 'RH', 'RS', 'RC', 'D', 'DH', 'S', '.'
    coc_collect: string | null;
    seer_collect: string | null;
    cccr_collect: string | null;
    // Version lifecycle
    year_implemented: number | null;
    version_implemented: string | null;
    is_retired: boolean;
    year_retired: number | null;
    version_retired: string | null;
    retired_reason: string | null;
    replaced_by_item: string | null;
    // Condition applicability
    applies_to_all_conditions: boolean;
    condition_applicability: string[] | null;
    created_at: string;
    // Populated via includes
    values?: StandardFieldValue[];
    alternate_names?: FieldAlternateName[];
}

// Structured code list entry for a field (from CodeList.csv)
export interface StandardFieldValue {
    id: string;
    standard_field_definition_id: string;
    code: string;
    description: string | null;
    sort_order: number;
    is_active: boolean;
    created_at: string;
}

// Alternate display name for a field (from AlternateNames.csv)
export interface FieldAlternateName {
    id: string;
    standard_field_definition_id: string;
    alternate_name: string;
    created_at: string;
}

// Clinical Ruleset (AJCC, NYHA, etc.)
export interface ClinicalRuleset {
    id: string;
    code: string;
    name: string;
    description: string | null;
    registry_domain: string;
    registry_standard_id: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

// Ruleset Edition (8th, 2022-rev, etc.)
export interface RulesetEdition {
    id: string;
    clinical_ruleset_id: string;
    edition_code: string;
    display_name: string;
    release_year: number | null;
    effective_date: string;
    end_date: string | null;
    description: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

// Condition Rule (Lung+8th, HFrEF+2022, etc.)
export interface ConditionRule {
    id: string;
    ruleset_edition_id: string;
    condition_code: string;
    condition_codes: string[] | null;
    condition_code_pattern: string | null;
    condition_name: string;
    rule_name: string;
    schema_id: string | null;
    rule_description: string | null;
    rule_metadata: Record<string, unknown> | null;
    histology_inclusions: Array<{ min: number; max: number }> | null;
    histology_exclusions: Array<{ exact?: number; min?: number; max?: number }> | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    _count?: { rule_field_mappings: number };
}

// Rule-to-Field Mapping
export interface RuleFieldMapping {
    id: string;
    condition_rule_id: string;
    standard_field_definition_id: string;
    field_role: 'input' | 'output' | 'both';
    extraction_instruction: string | null;
    is_required_for_classification: boolean;
    priority: number;
    created_at: string;
}

// Cross-Edition Value Crosswalk
export interface RulesetValueCrosswalk {
    id: string;
    from_edition_id: string;
    from_condition_code: string;
    from_field_name: string;
    from_value: string;
    to_edition_id: string;
    to_value: string;
    mapping_quality: 'exact' | 'approximate' | 'split' | 'merged' | 'no_equivalent';
    mapping_notes: string | null;
    is_one_to_many: boolean;
    confidence: number | null;
    created_at: string;
}

// Cross-Edition Stage Crosswalk
export interface RulesetStageCrosswalk {
    id: string;
    from_edition_id: string;
    from_condition_code: string;
    from_stage_group: string;
    from_stage_type: 'anatomic' | 'prognostic';
    to_edition_id: string;
    to_stage_group: string | null;
    to_stage_type: 'anatomic' | 'prognostic';
    mapping_quality: 'exact' | 'approximate' | 'split' | 'merged' | 'no_equivalent';
    mapping_notes: string | null;
    created_at: string;
}

// Layer 2: Compliance Program (SEER, NPCR, CMS, etc.)
export interface ComplianceProgram {
    id: string;
    code: string;
    name: string;
    description: string | null;
    registry_domain: string;
    registry_standard_id: string | null;
    governing_body: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

// Layer 3: Jurisdiction (US states, Canadian provinces, etc.)
export interface Jurisdiction {
    id: string;
    code: string;
    name: string;
    country_code: string;
    jurisdiction_type: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

// Composite context: jurisdiction + program + standard version
export interface JurisdictionProfile {
    id: string;
    jurisdiction_id: string;
    compliance_program_id: string | null;
    standard_version_id: string;
    display_name: string;
    effective_date: string;
    end_date: string | null;
    reporting_deadline_days: number | null;
    follow_up_required: boolean;
    follow_up_interval_months: number | null;
    description: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    // Populated via includes
    jurisdiction?: Jurisdiction;
    compliance_program?: ComplianceProgram | null;
    standard_version?: StandardVersion;
}

// Merged effective field from all 3 layers (base + clinical rule + jurisdiction override)
export interface EffectiveField {
    // Core identification
    item_number: string;
    name: string;
    display_name: string | null;

    // Effective requirement (cascade: base < rule mapping < jurisdiction override)
    effective_required: boolean;
    effective_severity: string | null;

    // Instructions (cascade: base < rule)
    base_instruction: string | null;
    rule_instruction: string | null;
    merged_instruction: string | null;

    // Valid code values
    valid_values: { code: string; description: string | null }[];

    // Data constraints (tells LLM what format to output)
    data_type: string | null;
    data_length: number | null;
    format: string | null;

    // Documentation (enriches prompt context)
    category: string | null;
    description: string | null;
    code_notes: string | null;
    clarification: string | null;
    alternate_names: string[];

    // Classification metadata (from rule_field_mapping)
    field_role: string | null;
    is_required_for_classification: boolean;
    priority: number;
}

// Per-profile field requirement overrides
export interface FieldRequirementOverride {
    id: string;
    jurisdiction_profile_id: string;
    standard_field_definition_id: string;
    override_type: 'requirement' | 'validation' | 'code_set' | 'additional';
    is_required: boolean | null;
    validation_severity: 'error' | 'warning' | 'info' | null;
    allowed_values_override: Record<string, unknown> | null;
    additional_instruction: string | null;
    description: string | null;
    effective_date: string | null;
    end_date: string | null;
    changed_by: string | null;
    change_reason: string | null;
    created_at: string;
    // Populated via includes
    standard_field_definition?: StandardFieldDefinition;
}
